import React, { forwardRef, useRef } from 'react';
import { composeRef } from 'rc-util/lib/ref';
import { Close, Search as SearchIcon } from '@nackle/origami-icons';
import { Input } from './input';
import { Button } from '../../button/button';
import { getString } from '@nackle/intl-tools';

/**
 * A search input for a form
 *
 * {@link https://ant.design/components/input#api}
 *
 * @module Search
 * @param {Object} props - the Search props
 * @param {string} [props.size] - the size of the search input
 * @param {string} [props.suffix] - the suffix of the search input
 * @param {bool} [props.enterButton] - whether or not the search input enter button displays
 * @param {bool} [props.addonAfter] - whether or not the search input adds on searched values after selecting
 * @param {bool} [props.loading] - whether or not search input loading icon displays
 * @param {bool} [props.disabled] - whether or not input is disabled
 * @param {Function} [props.onSearch] - function that calls on search
 * @param {Function} [props.onChange] - function that calls on input value change
 * @param {bool} [props.iconRight] - whether or not the search icon displays on the right
 * @param {string} [props.label] - label for search input
 * @param {Object} [props.labelProps] - props for search input label
 * @param {bool} [props.caption] - whether or not input caption displays
 * @param {Object} [props.captionProps] - props for input caption
 * @returns {React.ReactElement} - a search component for a form
**/
const Search = forwardRef( ( props, ref ) => {
    const {
        size,
        suffix,
        enterButton = false,
        addonAfter,
        loading,
        disabled,
        onSearch: customOnSearch,
        onChange: customOnChange,
        iconRight,
        label,
        labelProps,
        caption,
        captionProps,
        ...restProps
    } = props;

    const composedRef = useRef( false );
    const inputRef = useRef( null );

    const searchIcon = typeof enterButton === 'boolean' ? <SearchIcon /> : null;

    const onChange = ( e ) => {
        if ( e && e.target && e.type === 'click' && customOnSearch ) {
            customOnSearch( e.target.value, e );
        }
        if ( customOnChange ) {
            customOnChange( e );
        }
    };

    const onMouseDown = ( e ) => {
        if ( document.activeElement === inputRef.current?.input ) {
            e.preventDefault();
        }
    };

    const onSearch = ( e ) => {
        if ( customOnSearch ) {
            customOnSearch( inputRef.current?.input?.value, e );
        }
    };

    const onPressEnter = ( e ) => {
        if ( composedRef.current || loading ) {
            return;
        }
        onSearch( e );
    };
    labelProps ? labelProps.size = size : '';
    captionProps ? captionProps.size = size : '';
    const searchButton = (
        <Button
            className='ant-input-search-icon'
            type={ enterButton ? 'primary' : undefined }
            size={ size }
            disabled={ disabled }
            key='enterButton'
            onMouseDown={ onMouseDown }
            onClick={ onSearch }
            loading={ loading }
            icon={ searchIcon }
            aria-label={ getString( 'origami.input.search' ) }
        >
            { enterButton }
        </Button>
    );

    return (
        enterButton ? (
            <Input
                ref={ composeRef( inputRef, ref ) }
                className='ant-input-search'
                size={ size }
                onPressEnter={ onPressEnter }
                addonAfter={ searchButton }
                allowClear={ props.allowClear ? { clearIcon: <Close /> } : false }
                suffix={ suffix }
                onChange={ onChange }
                disabled={ disabled }
                label={ label ? label : undefined }
                labelProps={ label ? labelProps : undefined }
                caption={ caption ? caption : undefined }
                captionProps={ caption ? captionProps : undefined }
                { ...restProps }
            />
        ) : iconRight ? (
            <Input
                ref={ composeRef( inputRef, ref ) }
                size={ size }
                onPressEnter={ onPressEnter }
                className='ant-input-search'
                suffix={ searchButton }
                onChange={ onChange }
                disabled={ disabled }
                label={ label ? label : undefined }
                labelProps={ label ? labelProps : undefined }
                caption={ caption ? caption : undefined }
                captionProps={ caption ? captionProps : undefined }
                { ...restProps }
            />
        ) : (
            <Input
                ref={ composeRef( inputRef, ref ) }
                size={ size }
                onPressEnter={ onPressEnter }
                className='ant-input-search'
                prefix={ searchButton }
                onChange={ onChange }
                disabled={ disabled }
                label={ label ? label : undefined }
                labelProps={ label ? labelProps : undefined }
                caption={ caption ? caption : undefined }
                captionProps={ caption ? captionProps : undefined }
                { ...restProps }
            />
        )
    );
} );

export default Search;
